import { useState, useEffect, useMemo } from 'react';
import {
  Brain,
  Search,
  Filter,
  Calendar,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Phone,
  Activity,
  Shield,
  FileText,
  ArrowUpRight,
  Hash,
  Globe,
  Zap,
  FileSignature,
  Users
} from 'lucide-react';
import { useLanguage } from '../../hooks';
import { auditService } from '../../services/api';
import { getActivityMeta, formatActivityMessage, formatCurrency, formatDate } from '../../utils/helpers';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';

// Module-level cache to prevent refetching across tab switches
let cachedState = {
  logs: null,
  page: 1,
  hasMore: true,
  searchTerm: '',
  filterType: 'all',
  filterUser: 'all',
  dateRange: '7',
  loadedDateRange: null,
  customFrom: '',
  customTo: '',
  loadedCustomFrom: null,
  loadedCustomTo: null
};

// eslint-disable-next-line no-unused-vars
export default function AuditLogsView({ prequalifications, currentUser, users: allUsers = [], auditLogs = [], setAuditLogs }) {
  const { t } = useLanguage();
  const [logs, setLogs] = useState(cachedState.logs || []);
  const [loading, setLoading] = useState(!cachedState.logs);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(cachedState.page);
  const [hasMore, setHasMore] = useState(cachedState.hasMore);
  const [searchTerm, setSearchTerm] = useState(cachedState.searchTerm);
  const [filterType, setFilterType] = useState(cachedState.filterType);
  const [filterUser, setFilterUser] = useState(cachedState.filterUser);
  const [dateRange, setDateRange] = useState(cachedState.dateRange);
  const [customFrom, setCustomFrom] = useState(cachedState.customFrom || '');
  const [customTo, setCustomTo] = useState(cachedState.customTo || '');
  const [expandedLog, setExpandedLog] = useState(null);

  // Sync real-time logs from App.jsx
  useEffect(() => {
    if (auditLogs.length > 0) {
      setLogs(prev => {
        const newLogs = [...auditLogs, ...prev];
        return newLogs.filter((v, i, a) => a.findIndex(v2 => v2.id === v.id) === i);
      });
      if (setAuditLogs) setAuditLogs([]);
    }
  }, [auditLogs, setAuditLogs]);

  // Sync state to cache
  useEffect(() => {
    cachedState = { ...cachedState, logs, page, hasMore, searchTerm, filterType, filterUser, dateRange, customFrom, customTo };
  }, [logs, page, hasMore, searchTerm, filterType, filterUser, dateRange, customFrom, customTo]);

  const fetchLogs = async (pageNum = 1, append = false) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);

    try {
      let from, to;
      if (dateRange === 'custom') {
        from = customFrom ? new Date(customFrom) : new Date();
        to = customTo ? new Date(customTo) : new Date();
        // Set 'to' to end of day
        to.setHours(23, 59, 59, 999);
      } else {
        const days = parseInt(dateRange, 10) || 7;
        from = new Date();
        from.setDate(new Date().getDate() - days);
      }

      const params = { limit: 50, page: pageNum, dateFrom: from.toISOString() };
      if (to) {
        params.dateTo = to.toISOString();
      }

      const json = await auditService.getAll(params);

      if (!json.status) {
        if (!append) setLogs([]);
        return;
      }
      const fetchedLogs = json.data || [];
      if (append) {
        setLogs(prev => {
          const newLogs = [...prev, ...fetchedLogs];
          return newLogs.filter((v, i, a) => a.findIndex(v2 => v2.id === v.id) === i);
        });
      } else {
        setLogs(fetchedLogs);
      }
      setHasMore(fetchedLogs.length === 50);
      setPage(pageNum);
      cachedState.loadedDateRange = dateRange;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (cachedState.logs && cachedState.loadedDateRange === dateRange && cachedState.loadedCustomFrom === customFrom && cachedState.loadedCustomTo === customTo) return;
    if (dateRange === 'custom' && (!customFrom || !customTo)) return;
    cachedState.loadedCustomFrom = customFrom;
    cachedState.loadedCustomTo = customTo;
    fetchLogs(1, false);
  }, [dateRange, customFrom, customTo]);

  const handleRefresh = () => fetchLogs(1, false);

  const users = useMemo(() => {
    if (allUsers && allUsers.length > 0) {
      return Array.from(new Set(allUsers.map(u => u.username || u.email).filter(Boolean))).sort();
    }
    return [...new Set(logs.map(l => l.user?.username || l.user?.email).filter(Boolean))].sort();
  }, [logs, allUsers]);

  const interactionTypes = [
    { value: 'all', label: t('allTypes') || 'All Types' },
    { value: 'Prequalification', label: t('prequalifications') || 'Prequalifications' },
    { value: 'Tender', label: t('tendersProposals') || 'Tenders / Proposals' },
    { value: 'Contract', label: t('contracts') || 'Contracts' },
    { value: 'Profile', label: t('profiles') || 'Profiles' },
    { value: 'Call', label: t('followUp') || 'Follow Up' },
    { value: 'Source', label: t('sources') || 'Sources' },
    { value: 'City', label: t('cities') || 'Cities' },
    { value: 'Service', label: t('services') || 'Services' },
    { value: 'User', label: t('users') || 'Users' },
  ];

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // 'Call' filter also catches ADD_NOTE with type call_logged (stored as Prequalification entity)
      if (filterType === 'Call') {
        const isCallEntity = log.entity === 'Call';
        const isCallNote = log.entity === 'Prequalification' && log.action === 'ADD_NOTE' && log.metadata?.type === 'call_logged';
        if (!isCallEntity && !isCallNote) return false;
      } else if (filterType !== 'all' && log.entity !== filterType) {
        return false;
      }
      if (filterUser !== 'all' && (log.user?.username || log.user?.email) !== filterUser) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          log.action?.toLowerCase().includes(search) ||
          log.entity?.toLowerCase().includes(search) ||
          log.entityId?.toLowerCase().includes(search) ||
          JSON.stringify(log.metadata || {}).toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [logs, filterType, filterUser, searchTerm]);

  const stats = useMemo(() => {
    const byEntity = logs.filter(log => {
      // Don't use filteredLogs for stats — use all logs to show accurate counts per entity
      return true;
    }).reduce((acc, log) => {
      acc[log.entity] = (acc[log.entity] || 0) + 1;
      // Also count call_logged notes under 'Call'
      if (log.entity === 'Prequalification' && log.action === 'ADD_NOTE' && log.metadata?.type === 'call_logged') {
        acc['Call'] = (acc['Call'] || 0) + 1;
      }
      return acc;
    }, {});
    const uniqueUsers = new Set(logs.map(l => l.user?.username || l.user?.email).filter(Boolean)).size;
    return { total: logs.length, byEntity, uniqueUsers };
  }, [logs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { relative: '', full: '' };
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffS = Math.floor(diffMs / 1000);
    const diffM = Math.floor(diffS / 60);
    const diffH = Math.floor(diffM / 60);
    const diffD = Math.floor(diffH / 24);
    let relative = 'Just now';
    if (diffS >= 60 && diffM < 60) relative = `${diffM}m ago`;
    else if (diffM >= 60 && diffH < 24) relative = `${diffH}h ago`;
    else if (diffH >= 24 && diffD < 7) relative = `${diffD}d ago`;
    else if (diffD >= 7) relative = formatDate(date, { month: 'short', day: 'numeric' });
    return { relative, full: date.toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' }) };
  };

  const getEntityConfig = (entity) => {
    switch (entity) {
      case 'AI': return { icon: <Brain className="w-4 h-4" />, bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500', label: 'AI' };
      case 'Prequalification': return { icon: <FileText className="w-4 h-4" />, bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', label: 'Prequalification' };
      case 'Tender': return { icon: <ArrowUpRight className="w-4 h-4" />, bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500', label: 'Tender' };
      case 'Profile': return { icon: <User className="w-4 h-4" />, bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500', label: 'Profile' };
      case 'Contract': return { icon: <FileSignature className="w-4 h-4" />, bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Contract' };
      case 'Call': return { icon: <Phone className="w-4 h-4" />, bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', label: 'Follow Up' };
      case 'Source': return { icon: <Globe className="w-4 h-4" />, bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500', label: 'Source' };
      case 'City': return { icon: <Hash className="w-4 h-4" />, bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500', label: 'City' };
      case 'Service': return { icon: <Zap className="w-4 h-4" />, bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500', label: 'Service' };
      case 'Sources': return { icon: <Globe className="w-4 h-4" />, bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500', label: 'Sources' };
      case 'User': return { icon: <Users className="w-4 h-4" />, bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500', label: 'User' };
      case 'AppSetting': return { icon: <Shield className="w-4 h-4" />, bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500', label: 'App Settings' };
      default: return { icon: <Activity className="w-4 h-4" />, bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400', label: entity || 'System' };
    }
  };

  const getActionConfig = (action) => {
    const act = action?.toUpperCase() || '';
    if (act.includes('CREATE')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (act.includes('DELETE')) return 'bg-red-50 text-red-700 border-red-200';
    if (act.includes('UPDATE') || act.includes('TOGGLE')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (act.includes('IMPORT')) return 'bg-purple-50 text-purple-700 border-purple-200';
    if (act.includes('AI')) return 'bg-violet-50 text-violet-700 border-violet-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getUserNameById = (userId) => {
    if (!userId) return 'Unassigned';
    if (!allUsers || allUsers.length === 0) return userId;
    const user = allUsers.find(u => Number(u.id) === Number(userId));
    return user ? (user.username || user.email) : userId;
  };

  const getContactPhoneById = (contactId) => {
    if (!contactId) return 'N/A';
    if (!prequalifications || prequalifications.length === 0) return contactId;
    const leadWithContact = prequalifications.find(l => l.contact && Number(l.contact.id) === Number(contactId));
    if (leadWithContact && leadWithContact.contact?.phone) return leadWithContact.contact.phone;
    return contactId;
  };

  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return <span className="text-gray-400 italic">Empty</span>;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      const details = [];
      if (val.firstName || val.lastName) details.push(`${val.firstName || ''} ${val.lastName || ''}`.trim());
      else if (val.name) details.push(val.name);
      else if (val.username) details.push(val.username);
      else if (val.title) details.push(val.title);
      if (val.email) details.push(val.email);
      if (val.phone) details.push(val.phone);
      if (details.length > 0) return details.join(' • ');
      return <pre className="text-xs bg-gray-50 border p-1 rounded mt-1">{JSON.stringify(val, null, 2)}</pre>;
    }
    if (typeof val === 'string') {
      if (val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return new Date(val).toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' });
      }
      if ((val.startsWith('{') && val.endsWith('}')) || (val.startsWith('[') && val.endsWith(']'))) {
        try {
          const parsed = JSON.parse(val);
          return <pre className="text-[10px] bg-gray-50 border border-gray-100 p-2 rounded mt-1 font-mono text-gray-600 leading-relaxed overflow-x-auto">{JSON.stringify(parsed, null, 2)}</pre>;
        } catch (e) { /* not JSON */ }
      }
    }
    return String(val);
  };

  const renderDiff = (before, after) => {
    if (!before || !after) return null;
    const changes = [];
    Object.keys(after).forEach(key => {
      if (['updatedAt', 'createdAt', 'id', 'contactId', 'sourceId', 'assignedTo'].includes(key)) return;
      const bv = before[key], av = after[key];
      const isObj = typeof bv === 'object' || typeof av === 'object';
      const bs = isObj ? JSON.stringify(bv) : bv;
      const as = isObj ? JSON.stringify(av) : av;
      if (bs !== as && !(bv == null && av == null)) changes.push({ key, old: formatValue(bv), new: formatValue(av) });
    });
    if (changes.length === 0) return <p className="text-xs text-gray-400 italic py-2">No visible changes recorded</p>;
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider font-semibold">
            <tr>
              <th className="px-4 py-2.5">Field</th>
              <th className="px-4 py-2.5">Previous</th>
              <th className="px-4 py-2.5 text-blue-600">New Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {changes.map((c, idx) => (
              <tr key={idx} className="bg-white hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2.5 font-semibold text-gray-700 capitalize">{c.key.replace(/([A-Z])/g, ' $1').trim()}</td>
                <td className="px-4 py-2.5 text-gray-400 line-through">{c.old}</td>
                <td className="px-4 py-2.5 text-blue-700 font-medium bg-blue-50/30">{c.new}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const tryParseOrExtract = (jsonStr) => {
    if (!jsonStr || typeof jsonStr !== 'string') return {};
    try { return JSON.parse(jsonStr); } catch (e) {
      const result = {};
      const fromMatch = jsonStr.match(/"from"\s*:\s*"([^"]+)"/i); if (fromMatch) result.from = fromMatch[1];
      const toMatch = jsonStr.match(/"to"\s*:\s*"([^"]+)"/i); if (toMatch) result.to = toMatch[1];
      const amountMatch = jsonStr.match(/"amount"\s*:\s*(\d+(\.\d+)?)/i); if (amountMatch) result.amount = Number(amountMatch[1]);
      const typeMatch = jsonStr.match(/"paymentType"\s*:\s*"([^"]+)"/i); if (typeMatch) result.paymentType = typeMatch[1];
      const noteMatch = jsonStr.match(/"note"\s*:\s*"([^"]+)"/i); if (noteMatch) result.note = noteMatch[1];
      return result;
    }
  };

  const renderMetadata = (log) => {
    const { action, metadata } = log;
    if (!metadata) return null;

    if (action === 'UPDATE_LEAD' && metadata.before && metadata.after) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" />Modification History
          </div>
          {renderDiff(metadata.before, metadata.after)}
        </div>
      );
    }

    if (action === 'CREATE_LEAD' && metadata.prequalification) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <User className="w-3.5 h-3.5 text-emerald-500" />New Prequalification
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-semibold text-gray-400">Title</span>
              <span className="text-sm font-semibold text-gray-800">{metadata.prequalification.title || 'Untitled'}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-semibold text-gray-400">Status</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 uppercase w-fit">{metadata.prequalification.status}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-semibold text-gray-400">Phone</span>
              <span className="text-sm font-medium text-gray-700">{getContactPhoneById(metadata.contactId)}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-semibold text-gray-400">Assigned To</span>
              <span className="text-sm font-medium text-gray-700">{getUserNameById(metadata.prequalification.assignedTo)}</span>
            </div>
          </div>
        </div>
      );
    }

    if (action === 'CALL_LOGGED' || (action === 'UPDATE_LEAD' && metadata.outcome)) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <Phone className="w-3.5 h-3.5 text-amber-500" />Follow Up Summary
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase font-semibold text-gray-400">Outcome</span>
                <span className="text-sm font-bold text-amber-700">{metadata.outcome}</span>
              </div>
              {metadata.statusChangedTo && (
                <div className="flex flex-col gap-0.5 text-right">
                  <span className="text-[10px] uppercase font-semibold text-gray-400">Stage Update</span>
                  <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase">{metadata.statusChangedTo}</span>
                </div>
              )}
            </div>
            {metadata.notes && (
              <div className="pt-3 border-t border-gray-100">
                <span className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">Notes</span>
                <p className="text-sm text-gray-600 italic bg-gray-50 p-2.5 rounded-lg border border-gray-100">&#8220;{metadata.notes}&#8221;</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (action === 'PAYMENT_CREATED' || action === 'PAYMENT_DELETED') {
      const isDelete = action === 'PAYMENT_DELETED';
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <Sparkles className={`w-3.5 h-3.5 ${isDelete ? 'text-red-500' : 'text-emerald-500'}`} />Financial Record
          </div>
          <div className={`bg-white p-4 rounded-xl border ${isDelete ? 'border-red-200' : 'border-emerald-200'} shadow-sm flex items-center justify-between`}>
            <div>
              <p className="text-xl font-bold text-gray-900">
                <span className="text-xs font-medium text-gray-400 mr-1">PKR</span>
                {Number(metadata.amount || 0).toLocaleString()}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${isDelete ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{metadata.type}</span>
                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">· {metadata.shift} Shift</span>
              </div>
            </div>
            {metadata.hasNotes && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 border border-gray-200">
                <MessageSquare className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] text-gray-500 font-medium">Notes attached</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (action === 'IMPORT_LEADS_BULK') {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <RefreshCw className="w-3.5 h-3.5 text-purple-500" />Batch Import Statistics
          </div>
          <div className="flex gap-4">
            <div className="flex-1 bg-white p-4 rounded-xl border border-emerald-200 shadow-sm text-center">
              <div className="text-2xl font-bold text-emerald-600">{metadata.totalImported || 0}</div>
              <div className="text-[10px] text-emerald-700 uppercase font-semibold tracking-widest mt-1">Imported</div>
            </div>
            <div className="flex-1 bg-white p-4 rounded-xl border border-red-200 shadow-sm text-center">
              <div className="text-2xl font-bold text-red-600">{metadata.failedCount || 0}</div>
              <div className="text-[10px] text-red-700 uppercase font-semibold tracking-widest mt-1">Failed</div>
            </div>
          </div>
        </div>
      );
    }

    if (action === 'ADD_NOTE' && metadata.type && metadata.content) {
      const meta = getActivityMeta(metadata.type);
      const data = tryParseOrExtract(metadata.content);
      const displayContent = formatActivityMessage({ type: metadata.type, ...data });
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <span className="text-lg">{meta.icon}</span>{meta.label || 'Note Details'}
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-sm font-medium text-gray-800 leading-relaxed">{displayContent}</p>
            {typeof metadata.content === 'string' && metadata.content.includes('...') && (
              <p className="text-[10px] text-gray-400 mt-2 italic">* Some details may be truncated.</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <AlertCircle className="w-3.5 h-3.5 text-gray-400" />Extended Details
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          {typeof metadata === 'object' && !Array.isArray(metadata) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {Object.entries(metadata).map(([k, v]) => (
                <div key={k} className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-tight">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="text-sm text-gray-700 font-medium break-words">{formatValue(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <pre className="text-xs text-gray-600 overflow-x-auto font-mono bg-gray-50 p-3 rounded-lg border border-gray-100">{JSON.stringify(metadata, null, 2)}</pre>
          )}
        </div>
      </div>
    );
  };

  const getActionSummary = (log) => {
    const { action, metadata } = log;

    if (action === 'ADD_NOTE' && metadata?.type && metadata?.content) {
      const data = tryParseOrExtract(metadata.content);
      const formatted = formatActivityMessage({ type: metadata.type, ...data });
      if ((formatted === 'Activity logged' || formatted.startsWith('{')) && metadata.type) {
        return metadata.type.replace(/_/g, ' ');
      }
      return formatted;
    }
    if (action === 'CALL_LOGGED' && metadata?.outcome) return `Follow Up: ${metadata.outcome}`;
    if (action === 'CREATE_CONTRACT') return `Contract added · ${metadata?.contractNumber || metadata?.title || ''}`;
    if (action === 'UPDATE_CONTRACT') return `Contract updated · ${metadata?.contractNumber || metadata?.title || ''}`;
    if (action === 'PAYMENT_CREATED' && metadata?.amount) return `Payment Added · PKR ${Number(metadata.amount).toLocaleString()}`;
    if (action === 'PAYMENT_DELETED' && metadata?.amount) return `Payment Deleted · PKR ${Number(metadata.amount).toLocaleString()}`;
    if (action === 'UPDATE_LEAD') return 'Updated prequalification';
    if (action === 'CREATE_LEAD') return 'New prequalification created';
    if (action === 'DELETE_LEAD') return 'Prequalification deleted';
    if (action === 'IMPORT_LEADS_BULK') return `Bulk import · ${metadata?.totalImported || 0} records`;
    if (action === 'AI_RESPONSE') return 'AI response generated';
    // Tender actions
    if (action === 'CREATE_TENDER') return `Tender/Proposal created${metadata?.title ? ` · ${metadata.title}` : ''}`;
    if (action === 'UPDATE_TENDER') return `Tender/Proposal updated${metadata?.title ? ` · ${metadata.title}` : ''}`;
    if (action === 'DELETE_TENDER') return `Tender/Proposal deleted${metadata?.title ? ` · ${metadata.title}` : ''}`;
    // Profile actions
    if (action === 'CREATE_PROFILE') return `Profile created${metadata?.clientName ? ` · ${metadata.clientName}` : ''}`;
    if (action === 'UPDATE_PROFILE') return `Profile updated${metadata?.clientName ? ` · ${metadata.clientName}` : ''}`;
    if (action === 'DELETE_PROFILE') return `Profile deleted${metadata?.clientName ? ` · ${metadata.clientName}` : ''}`;
    // Source actions
    if (action === 'CREATE_SOURCE') return `Source added · ${metadata?.name || ''}`;
    if (action === 'UPDATE_SOURCE') return `Source renamed · ${metadata?.oldName || ''} → ${metadata?.newName || ''}`;
    if (action === 'DELETE_SOURCE') return `Source deleted · ${metadata?.name || ''}`;
    // City actions
    if (action === 'CREATE_CITY') return `City added · ${metadata?.name || ''}`;
    if (action === 'UPDATE_CITY') return `City renamed · ${metadata?.oldName || ''} → ${metadata?.newName || ''}`;
    if (action === 'TOGGLE_CITY') return `City ${metadata?.isActive ? 'activated' : 'deactivated'} · ${metadata?.name || ''}`;
    if (action === 'DELETE_CITY') return `City deleted · ${metadata?.name || ''}`;
    // Service actions
    if (action === 'CREATE_SERVICE') return `Service added · ${metadata?.name || ''}`;
    if (action === 'UPDATE_SERVICE') return `Service renamed · ${metadata?.oldName || ''} → ${metadata?.newName || ''}`;
    if (action === 'DELETE_SERVICE') return `Service deleted · ${metadata?.name || ''}`;
    // User actions
    if (action === 'CREATE_USER' || action === 'CREATE_USER_BATCH') return `User created · ${metadata?.username || metadata?.teamLeadsCount + ' Users'}`;
    if (action === 'UPDATE_USER') return `User updated · ${metadata?.username || ''}`;
    if (action === 'DELETE_USER') return `User deleted · ${metadata?.username || ''}`;
    if (action === 'CHANGE_USER_PASSWORD' || action === 'RESET_USER_PASSWORD') return 'User password changed';
    if (action === 'ACTIVATE_USER' || action === 'DEACTIVATE_USER') return `User ${action === 'ACTIVATE_USER' ? 'activated' : 'deactivated'} · ${metadata?.username || ''}`;
    // App Setting actions
    if (action === 'UPDATE_SETTING') return `App Setting updated · ${metadata?.key || ''}`;
    return log.action?.replace(/_/g, ' ') || 'System Operation';
  };

  const statCards = [
    { label: t('totalEvents') || 'Total Events', value: stats.total, icon: <Activity className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />, iconBg: 'bg-blue-100' },
    { label: t('usersConfigured') || 'Users Configured', value: stats.uniqueUsers, icon: <User className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />, iconBg: 'bg-purple-100' },
    { label: t('prequalifications') || 'Prequalifications', value: stats.byEntity['Prequalification'] || 0, icon: <FileText className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />, iconBg: 'bg-indigo-100' },
    { label: t('tendersProposals') || 'Tenders / Proposals', value: stats.byEntity['Tender'] || 0, icon: <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />, iconBg: 'bg-indigo-100' },
    { label: t('profiles') || 'Profiles', value: stats.byEntity['Profile'] || 0, icon: <User className="w-4 h-4 md:w-5 md:h-5 text-rose-600" />, iconBg: 'bg-rose-100' },
    { label: t('followUp') || 'Follow Up', value: (stats.byEntity['Call'] || 0), icon: <Phone className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />, iconBg: 'bg-amber-100' },
    { label: t('contracts') || 'Contracts', value: stats.byEntity['Contract'] || 0, icon: <FileSignature className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />, iconBg: 'bg-emerald-100' },
    { label: t('settings') || 'Settings', value: (stats.byEntity['Source'] || 0) + (stats.byEntity['City'] || 0) + (stats.byEntity['Service'] || 0) + (stats.byEntity['User'] || 0), icon: <Shield className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />, iconBg: 'bg-gray-100' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {t('auditLogs') || 'Audit Logs'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {t('viewSystemAuditLogs') || 'Complete history of all system activities and changes'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || loadingMore}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading && !cachedState.logs ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{t('refresh') || 'Refresh'}</span>
        </button>
      </div>

      {/* ── Stats Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-3">
        {loading ? (
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
                <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg mb-2" />
                <Skeleton width="60%" height={24} animation="wave" className="mt-2 md:mt-3" />
                <Skeleton width="40%" height={12} animation="wave" className="mt-1" />
              </div>
            ))}
          </>
        ) : (
          <>
            {statCards.map((card, idx) => (
              <div key={idx} className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
                <div className={`p-1.5 md:p-2 ${card.iconBg} rounded-lg w-fit`}>{card.icon}</div>
                <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3">{card.value}</p>
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">{card.label}</p>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Filters Bar ── */}
      <div className="flex flex-wrap items-center gap-2 bg-white p-2 md:p-3 rounded-xl border">
        <Filter className="w-4 h-4 text-gray-400 my-auto flex-shrink-0" />

        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder={t('searchLogs') || "Search logs..."}
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="h-6 w-px bg-gray-200 hidden sm:block" />

        <Dropdown className="w-auto">
          <DropdownTrigger>
            <button className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
              {interactionTypes.find(tp => tp.value === filterType)?.label || filterType}
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </DropdownTrigger>
          <DropdownPopover className="w-[180px]">
            <DropdownMenu selectedKeys={new Set([filterType])} selectionMode="single" onSelectionChange={(keys) => setFilterType(Array.from(keys)[0])}>
              {interactionTypes.map(type => (
                <DropdownItem key={type.value} id={type.value}>{type.label}</DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>

        <Dropdown className="w-auto">
          <DropdownTrigger>
            <button className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
              <User className="w-3.5 h-3.5 text-gray-500" />
              {filterUser === 'all' ? (t('allUsers') || 'All Users') : filterUser}
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </DropdownTrigger>
          <DropdownPopover className="w-[180px]">
            <DropdownMenu selectedKeys={new Set([filterUser])} selectionMode="single" onSelectionChange={(keys) => setFilterUser(Array.from(keys)[0])}>
              <DropdownItem id="all">{t('allUsers') || 'All Users'}</DropdownItem>
              {users.map(user => (<DropdownItem key={user} id={user}>{user}</DropdownItem>))}
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>

        <Dropdown className="w-auto">
          <DropdownTrigger>
            <button className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              {dateRange === '1' ? (t('last24Hours') || 'Last 24h') : dateRange === '7' ? (t('last7Days') || 'Last 7 Days') : dateRange === '30' ? (t('last30Days') || 'Last 30 Days') : dateRange === '90' ? (t('last90Days') || 'Last 90 Days') : (t('customDate') || 'Custom Date')}
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </DropdownTrigger>
          <DropdownPopover className="w-[160px]">
            <DropdownMenu selectedKeys={new Set([dateRange])} selectionMode="single" onSelectionChange={(keys) => setDateRange(Array.from(keys)[0])}>
              <DropdownItem id="1">{t('last24Hours') || 'Last 24 Hours'}</DropdownItem>
              <DropdownItem id="7">{t('last7Days') || 'Last 7 Days'}</DropdownItem>
              <DropdownItem id="30">{t('last30Days') || 'Last 30 Days'}</DropdownItem>
              <DropdownItem id="90">{t('last90Days') || 'Last 90 Days'}</DropdownItem>
              <DropdownItem id="custom">{t('customDate') || 'Custom Date'}</DropdownItem>
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>

        {dateRange === 'custom' && (
          <div className="flex items-center gap-2 mt-2 sm:mt-0 w-full sm:w-auto ml-auto">
            <div className="w-[140px]">
              <DatePicker
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                size="sm"
              />
            </div>
            <span className="text-gray-400">-</span>
            <div className="w-[140px]">
              <DatePicker
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                size="sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Logs Table ── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">

        {/* Desktop Table Header */}
        <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_40px] gap-4 px-5 py-3 bg-gray-50 border-b text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
          <span>{t('activity') || 'Activity'}</span>
          <span>{t('entity') || 'Entity'}</span>
          <span>{t('user') || 'User'}</span>
          <span>{t('time') || 'Time'}</span>
          <span />
        </div>

        {loading ? (
          <div className="divide-y divide-gray-100">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex gap-4 p-4 md:p-5">
                <Skeleton variant="rectangular" width={40} height={40} className="rounded-lg flex-shrink-0" animation="wave" />
                <div className="flex-1 space-y-2 py-0.5">
                  <Skeleton width="55%" height={16} animation="wave" />
                  <Skeleton width="35%" height={14} animation="wave" />
                </div>
                <div className="hidden md:block"><Skeleton width={80} height={16} animation="wave" /></div>
              </div>
            ))}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-800 mb-1">{t('noAuditLogsFound') || 'No audit logs found'}</p>
            <p className="text-sm text-gray-500 max-w-xs">
              {searchTerm || filterType !== 'all' || filterUser !== 'all'
                ? (t('tryAdjustingFilters') || 'Try adjusting your filters to see more results.')
                : (t('systemActivityWillAppearHere') || 'System activity will appear here as actions are performed.')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredLogs.map((log) => {
              const ts = formatTimestamp(log.createdAt);
              const isExpanded = expandedLog === log.id;
              const ec = getEntityConfig(log.entity);
              const ac = getActionConfig(log.action);

              return (
                <div key={log.id} className={`transition-colors ${isExpanded ? 'bg-blue-50/20' : 'hover:bg-gray-50/70'}`}>
                  <button onClick={() => setExpandedLog(isExpanded ? null : log.id)} className="w-full text-left focus:outline-none">

                    {/* Mobile */}
                    <div className="md:hidden p-4">
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${ec.bg} ${ec.text} flex items-center justify-center border ${ec.border}`}>
                          {ec.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${ac}`}>{log.action?.replace(/_/g, ' ')}</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${ec.bg} ${ec.text} ${ec.border}`}>{ec.label}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 truncate">{getActionSummary(log)}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-xs text-gray-500">{log.user?.username || 'System'}</span>
                            <span className="text-xs text-gray-400">{ts.relative}</span>
                          </div>
                        </div>
                        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isExpanded ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </div>
                      </div>
                    </div>

                    {/* Desktop */}
                    <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_40px] gap-4 px-5 py-3.5 items-center">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${ec.bg} ${ec.text} flex items-center justify-center border ${ec.border}`}>
                          {ec.icon}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{getActionSummary(log)}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${ac}`}>{log.action?.replace(/_/g, ' ')}</span>
                            {log.prequalification && (
                              <span className="text-[11px] text-gray-500 font-medium truncate">
                                {log.prequalification.profile?.clientName || log.prequalification.profile?.contactPersonName || ''}
                              </span>
                            )}
                            {log.profile && !log.prequalification && (
                              <span className="text-[11px] text-gray-500 font-medium truncate">
                                {log.profile.clientName || log.profile.contactPersonName || ''}
                              </span>
                            )}
                            {!log.prequalification && !log.profile && log.entityId && (
                              <span className="flex items-center gap-0.5 text-[11px] text-gray-400 font-mono">
                                <Hash className="w-3 h-3" />{log.entityId.slice(-8)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${ec.bg} ${ec.text} ${ec.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${ec.dot}`} />
                          {ec.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-gray-500" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium truncate">{log.user?.username || log.user?.email || 'System'}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 font-medium">{ts.relative}</span>
                        <span className="text-[11px] text-gray-400">{ts.full}</span>
                      </div>

                      <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${isExpanded ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Panel */}
                  {isExpanded && (
                    <div className="px-4 md:px-5 pb-5 border-t border-gray-100 bg-gray-50/50">
                      <div className="pt-4 space-y-4">
                        {/* Meta tags row */}
                        <div className="flex flex-wrap gap-2">
                          {log.user && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              <span className="font-medium">{log.user.username || log.user.email}</span>
                              {log.user.role && <span className="text-gray-400">· {log.user.role}</span>}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            <span className="font-medium">{ts.full}</span>
                          </div>
                          {log.entityId && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-1.5 font-mono">
                              <Hash className="w-3.5 h-3.5" />{log.entityId}
                            </div>
                          )}
                          {(log.ipAddress) && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                              <Globe className="w-3.5 h-3.5 text-gray-400" />{log.ipAddress}
                            </div>
                          )}
                        </div>
                        {renderMetadata(log)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-widest">
              {t('live') || 'Live'} · {filteredLogs.length} {t('events') || 'events'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
